'use client';

import { type ReactNode, createContext, useCallback, useEffect, useMemo, useState } from 'react';

export type SoundId = 'explode' | 'jump' | 'lose' | 'score' | 'shoot' | 'tap' | 'win';

export interface AudioContextValue {
  isMuted: boolean;
  playSound: (soundId: SoundId) => void;
  setMuted: (muted: boolean) => void;
  toggleMuted: () => void;
}

export const AudioContext = createContext<AudioContextValue | null>(null);

interface AudioProviderProps {
  children: ReactNode;
}

/**
 * Get initial muted state from localStorage.
 */
const getInitialMutedState = (): boolean => {
  if (typeof window === 'undefined') return false;
  const stored = localStorage.getItem('kidgameland-muted');
  return stored === 'true';
};

/**
 * Audio provider for managing game sounds.
 * Uses Web Audio API for low-latency sound playback.
 * @param props - Provider props.
 * @param props.children - Child components.
 */
export const AudioProvider = ({ children }: AudioProviderProps) => {
  const [isMuted, setIsMuted] = useState(getInitialMutedState);
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);

  /** Initialize audio context on first user interaction */
  useEffect(() => {
    const initAudio = () => {
      if (!audioContext) {
        const ctx = new (window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
        setAudioContext(ctx);
      }
      /** Remove listener after first interaction */
      document.removeEventListener('touchstart', initAudio);
      document.removeEventListener('click', initAudio);
    };
    document.addEventListener('touchstart', initAudio, { once: true });
    document.addEventListener('click', initAudio, { once: true });
    return () => {
      document.removeEventListener('touchstart', initAudio);
      document.removeEventListener('click', initAudio);
    };
  }, [audioContext]);

  const setMuted = useCallback((muted: boolean) => {
    setIsMuted(muted);
    localStorage.setItem('kidgameland-muted', String(muted));
  }, []);

  const toggleMuted = useCallback(() => {
    setMuted(!isMuted);
  }, [isMuted, setMuted]);

  /** Simple synthesized sounds using Web Audio API */
  const playSound = useCallback(
    (soundId: SoundId) => {
      if (isMuted || !audioContext) return;
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      const now = audioContext.currentTime;
      switch (soundId) {
        case 'explode':
          /** Explosion noise */
          oscillator.type = 'sawtooth';
          oscillator.frequency.setValueAtTime(150, now);
          oscillator.frequency.exponentialRampToValueAtTime(50, now + 0.3);
          gainNode.gain.setValueAtTime(0.4, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
          oscillator.start(now);
          oscillator.stop(now + 0.3);
          break;
        case 'jump':
          /** Quick upward sweep */
          oscillator.frequency.setValueAtTime(300, now);
          oscillator.frequency.exponentialRampToValueAtTime(600, now + 0.15);
          gainNode.gain.setValueAtTime(0.3, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
          oscillator.start(now);
          oscillator.stop(now + 0.15);
          break;
        case 'lose':
          /** Sad descending tone */
          oscillator.frequency.setValueAtTime(400, now);
          oscillator.frequency.exponentialRampToValueAtTime(200, now + 0.3);
          gainNode.gain.setValueAtTime(0.3, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
          oscillator.start(now);
          oscillator.stop(now + 0.3);
          break;
        case 'score':
          /** Ding sound */
          oscillator.frequency.setValueAtTime(880, now);
          gainNode.gain.setValueAtTime(0.3, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
          oscillator.start(now);
          oscillator.stop(now + 0.2);
          break;
        case 'shoot':
          /** Pew pew sound */
          oscillator.type = 'square';
          oscillator.frequency.setValueAtTime(1200, now);
          oscillator.frequency.exponentialRampToValueAtTime(100, now + 0.1);
          gainNode.gain.setValueAtTime(0.2, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
          oscillator.start(now);
          oscillator.stop(now + 0.1);
          break;
        case 'tap':
          /** Short click sound */
          oscillator.frequency.setValueAtTime(800, now);
          oscillator.frequency.exponentialRampToValueAtTime(600, now + 0.1);
          gainNode.gain.setValueAtTime(0.3, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
          oscillator.start(now);
          oscillator.stop(now + 0.1);
          break;
        case 'win':
          /** Happy ascending melody */
          oscillator.frequency.setValueAtTime(523, now); // C5
          oscillator.frequency.setValueAtTime(659, now + 0.15); // E5
          oscillator.frequency.setValueAtTime(784, now + 0.3); // G5
          oscillator.frequency.setValueAtTime(1047, now + 0.45); // C6
          gainNode.gain.setValueAtTime(0.4, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.6);
          oscillator.start(now);
          oscillator.stop(now + 0.6);
          break;
      }
    },
    [isMuted, audioContext]
  );

  const value = useMemo(
    () => ({
      isMuted,
      playSound,
      setMuted,
      toggleMuted
    }),
    [isMuted, setMuted, toggleMuted, playSound]
  );

  return <AudioContext.Provider value={value}>{children}</AudioContext.Provider>;
};
